import { create } from 'zustand';
import type { ClientAuthScope, UserInfo } from '@/models/auth';

interface DemoUser {
  email: string;
  role: string;
}

interface AuthState {
  user: unknown;
  userInfo: UserInfo | null;
  loading: boolean;
  demoUser: DemoUser | null;
  availableScopes: ClientAuthScope[];
  activeScope: ClientAuthScope | null;
  activeScopeKey: string | null;
  setUser: (user: unknown) => void;
  setUserInfo: (info: UserInfo | null) => void;
  setLoading: (loading: boolean) => void;
  setDemoUser: (user: DemoUser) => void;
  setAvailableScopes: (scopes: ClientAuthScope[]) => void;
  setActiveScope: (scope: ClientAuthScope | null) => void;
  clearScopes: () => void;
  clearDemoUser: () => void;
  signOut: () => void;
}

const DEMO_USER_KEY = 'classifai_demo_user';
const ACTIVE_SCOPE_KEY = 'classifai_active_scope';

function loadDemoUser(): DemoUser | null {
  try {
    const raw = sessionStorage.getItem(DEMO_USER_KEY);
    return raw ? (JSON.parse(raw) as DemoUser) : null;
  } catch {
    return null;
  }
}

function saveDemoUser(user: DemoUser | null) {
  if (user) {
    sessionStorage.setItem(DEMO_USER_KEY, JSON.stringify(user));
  } else {
    sessionStorage.removeItem(DEMO_USER_KEY);
  }
}

function loadActiveScopeKey(): string | null {
  try {
    return sessionStorage.getItem(ACTIVE_SCOPE_KEY);
  } catch {
    return null;
  }
}

function saveActiveScopeKey(scopeKey: string | null) {
  if (scopeKey) {
    sessionStorage.setItem(ACTIVE_SCOPE_KEY, scopeKey);
  } else {
    sessionStorage.removeItem(ACTIVE_SCOPE_KEY);
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  userInfo: null,
  loading: true,
  demoUser: loadDemoUser(),
  availableScopes: [],
  activeScope: null,
  activeScopeKey: loadActiveScopeKey(),
  setUser: (user) => set({ user }),
  setUserInfo: (userInfo) => set({ userInfo }),
  setLoading: (loading) => set({ loading }),
  setDemoUser: (demoUser) => {
    saveDemoUser(demoUser);
    set({ demoUser });
  },
  setAvailableScopes: (availableScopes) => set({ availableScopes }),
  setActiveScope: (activeScope) => {
    saveActiveScopeKey(activeScope?.scopeKey ?? null);
    set({
      activeScope,
      activeScopeKey: activeScope?.scopeKey ?? null,
    });
  },
  clearScopes: () => {
    saveActiveScopeKey(null);
    set({
      availableScopes: [],
      activeScope: null,
      activeScopeKey: null,
    });
  },
  clearDemoUser: () => {
    saveDemoUser(null);
    set({ demoUser: null });
  },
  signOut: () => {
    saveDemoUser(null);
    saveActiveScopeKey(null);
    set({
      user: null,
      userInfo: null,
      demoUser: null,
      availableScopes: [],
      activeScope: null,
      activeScopeKey: null,
    });
  },
}));
